/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { useReactiveVar } from '@apollo/client';
import { langIdVar, langVar, loggedInVar } from '@graphql/cache';
import { CustomerQuery, useCustomerQuery } from '@queries/__generated__/customer';
import * as Sentry from '@sentry/nextjs';
import { mapLocaleToLangId } from '@utils/i18n';
import { useRouter } from 'next/router';
import React, { FC, PropsWithChildren, useEffect, useMemo } from 'react';

export type CustomerContextValue = {
  loading: boolean;
  loggedIn: boolean;
  customer: CustomerQuery['customer'] | null;
};

export const CustomerContext = React.createContext<CustomerContextValue>({
  loading: true,
  loggedIn: false,
  customer: null,
});

CustomerContext.displayName = 'CustomerContext';

type CustomerWrapperProps = PropsWithChildren<object>;

export const CustomerWrapper: FC<CustomerWrapperProps> = ({ children }) => {
  const { locale } = useRouter();
  const loggedIn = useReactiveVar(loggedInVar);
  const { loading, data } = useCustomerQuery({
    skip: !loggedIn,
    onCompleted(data) {
      const { username } = data?.customer;

      // Set the user's name to track in Sentry
      Sentry.setUser({ username });
    },
  });

  const state: CustomerContextValue = useMemo(() => {
    const loggedIn = !!data?.customer?.loggedIn;
    return {
      loading: !data,
      loggedIn,
      customer: data?.customer ?? null,
    };
  }, [loading, data]);

  // Update the lang variables when the locale changes
  useEffect(() => {
    const langId = mapLocaleToLangId(locale);
    langVar(locale);
    langIdVar(langId);
  }, [locale]);

  return <CustomerContext.Provider value={state}>{children}</CustomerContext.Provider>;
};

export const useCustomer = () => {
  const context = React.useContext(CustomerContext);
  if (context === undefined) {
    throw new Error(`useCustomer must be used within a CustomerWrapper`);
  }
  return context;
};
